import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

/**
 * App-wide language preference (EN / AR), shared by the login screen and the
 * authenticated app header so the choice carries across the auth boundary
 * instead of resetting on sign-in.
 *
 * Arabic flips the whole document: `<html lang="ar" dir="rtl">`. UI strings
 * come from src/i18n (useT). Chat messages still carry their own `dir` from
 * the message's content (utils/textDirection), so an English reply renders
 * LTR inside the RTL page. Styles that must follow an element's *own*
 * direction use `:dir(rtl)` or `.x[dir="rtl"]` on that element — never an
 * ancestor `[dir='rtl'] .x`, which would now match <html> for every message.
 */

export type Lang = 'en' | 'ar';

const STORAGE_KEY = 'loginLang';

export const readStoredLang = (): Lang => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'ar' ? 'ar' : 'en';
  } catch {
    // Private-mode / blocked storage should not stop the page from rendering.
    return 'en';
  }
};

const storeLang = (lang: Lang): void => {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* non-fatal */
  }
};

interface LanguageContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Lang>(readStoredLang);

  // Drives screen-reader pronunciation, the :lang(ar) typography rules in
  // index.css, and right-to-left layout for the whole app.
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  }, [lang]);

  const setLang = (next: Lang) => {
    setLangState(next);
    storeLang(next);
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang }}>
      {children}
    </LanguageContext.Provider>
  );
};

/** Outside a provider (unit tests rendering a component alone): English, no-op setter. */
const FALLBACK: LanguageContextType = { lang: 'en', setLang: () => {} };

export const useLanguage = (): LanguageContextType => useContext(LanguageContext) ?? FALLBACK;
